import { useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { useAuth } from '@argo/common/auth'
import { SessionContext, loadAccount, saveAccount } from '@argo/common/session'
import Layout from './components/Layout'
import Login from '@argo/common/pages/Login'
import Register, { VerifyEmail } from '@argo/common/pages/Register'
import Forgot, { ResetFromLink } from '@argo/common/pages/Forgot'
import Accounts from './pages/Accounts'
import Account from './pages/Account'
import Exchange from './pages/Exchange'
import Ramp from './pages/Ramp'
import Events from './pages/Events'
import Settings from './pages/Settings'

export default function App() {
  const { ready, authed } = useAuth()

  if (!ready) {
    return (
      <div className="auth">
        <div className="auth-card" style={{ display: 'grid', gap: 12 }}>
          <div className="skeleton" style={{ height: 38, width: '55%' }} />
          <div className="skeleton" style={{ height: 14 }} />
          <div className="skeleton" style={{ height: 14, width: '70%' }} />
        </div>
      </div>
    )
  }
  if (!authed) return <SignedOut />
  return <Session />
}

/**
 * Signed out, which is three screens rather than one: sign in, create an
 * account, and the page the emailed confirmation link lands on. None of them
 * needs a token, so they live above the auth check.
 */
const HOME = import.meta.env.BASE_URL || '/'

/** Where the URL currently points, as state rather than a live read. */
function readRoute() {
  return {
    path: window.location.pathname,
    token: new URLSearchParams(window.location.search).get('token'),
  }
}

function SignedOut() {
  // The route has to live in state. Reading window.location during render and
  // leaving home() to call replaceState looks equivalent and is not: history
  // changes do not re-render, and the setView('in') beside it is a no-op on
  // these screens because view is already 'in' — the route came from the URL,
  // not from view. React bails out on identical state, so the address bar
  // changed and the screen did not, which is exactly what "Sign in" did after
  // a password reset.
  const [route, setRoute] = useState(readRoute)
  const [view, setView] = useState<'in' | 'up' | 'forgot'>('in')

  // Back and forward should still work once a link has been consumed.
  useEffect(() => {
    const onPop = () => { setRoute(readRoute()); setView('in') }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const home = () => {
    window.history.replaceState(null, '', HOME)
    setRoute({ path: HOME, token: null })   // a new object, so this always renders
    setView('in')
  }

  // Both links arrive from an email, so they are read from the URL before
  // anything else and cannot depend on state this tab does not have.
  if (route.path.endsWith('/verify-email') && route.token)
    return <VerifyEmail token={route.token} onDone={home} />
  if (route.path.endsWith('/reset-password') && route.token)
    return <ResetFromLink token={route.token} onDone={home} />

  if (view === 'up') return <Register onDone={home} onCancel={() => setView('in')} />
  if (view === 'forgot') return <Forgot onDone={home} />
  return <Login onRegister={() => setView('up')} onForgot={() => setView('forgot')} />
}

/** Signed in: resolve who the caller is, then render the app shell. */
function Session() {
  const [account, setAccountState] = useState(loadAccount)
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 })

  // A customer always acts as their own account; an operator may pick one.
  useEffect(() => {
    if (!me.data) return
    const operator = me.data.roles.includes('operator')
    if (!operator || !account) {
      saveAccount(me.data.account)
      setAccountState(me.data.account)
    }
  }, [me.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const session = useMemo(() => ({
    account,
    setAccount: (a: string) => { saveAccount(a); setAccountState(a) },
    me: me.data,
  }), [account, me.data])

  return (
    <SessionContext.Provider value={session}>
      <Layout>
        <Routes>
          <Route path="/" element={<Exchange />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/g/*" element={<Accounts />} />
          <Route path="/a/:account" element={<Account />} />
          <Route path="/ramp" element={<Ramp />} />
          <Route path="/events" element={<Events />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </SessionContext.Provider>
  )
}
